import {
  OHNE_TAETIGKEIT, OPT_BERUEHRUNGSPUNKTE, OPT_BERUFSSTATUS, OPT_FAMILIENSTAND, OPT_WICHTIG, OPT_ZIELE,
} from "../state/schema";
import { card, chipGroup, h, jaNein, note, selectInput, textareaInput, textInput } from "../components/ui";
import { vBerufFamilie, vStammdaten, vZiele } from "../logic/validation";
import { Step } from "./types";

/* ---------- 1 · Bienvenida (Hero) ---------- */
export const stepWelcome: Step = {
  id: "welcome",
  section: null,
  chrome: false,
  title: "Tu Análisis de Valor",
  build(ctx) {
    const fact = (icon: string, title: string, text: string) =>
      h("div", { class: "fact" },
        h("div", { class: "fact__icon", text: icon }),
        h("div", {}, h("div", { class: "fact__title", text: title }), h("div", { class: "fact__text", text: text })));

    return h(
      "div",
      { class: "hero" },
      h("img", { class: "hero__logo", src: "/assets/logo-lockup.png", alt: "Blacksmith Finance" }),
      h("h1", { class: "hero__title", text: "Tu Análisis de Valor" }),
      h("p", {
        class: "hero__lead",
        text:
          "Con este Análisis de Valor registramos tu situación financiera actual, tu protección, tus objetivos y tus contratos existentes. Así podemos preparar tu próxima asesoría de forma precisa y eficiente.",
      }),
      h(
        "div",
        { class: "hero__facts" },
        fact("⏱️", "Unos 10–15 minutos", "Con calma, paso a paso: puedes volver atrás en cualquier momento."),
        fact("📄", "Ten a mano tus documentos", "Tu nómina reciente, las pólizas de seguro y la factura de luz o gas."),
        fact("💾", "Se guarda automáticamente", "Tus datos se conservan mientras esta página esté abierta."),
        fact("⬆️", "Sube los documentos más adelante", "Subirás los documentos necesarios cómodamente durante el proceso."),
      ),
      h("button", { class: "btn btn--primary hero__cta", type: "button", text: "Comenzar el Análisis de Valor", on: { click: () => ctx.goNext() } }),
      h("p", { class: "field__optional", style: { marginTop: "14px", textAlign: "center" }, text: "Tus datos se tratan de forma confidencial y solo se usan para preparar tu asesoría." })
    );
  },
};

/* ---------- 2 · Objetivos y expectativas ---------- */
export const stepZiele: Step = {
  id: "ziele",
  section: "fundament",
  eyebrow: "Fundamentos",
  title: "Nuestros valores, tu rumbo",
  subtitle: "Cuéntanos brevemente qué buscas. Puedes elegir varias opciones.",
  leaveMessage: "Tu rumbo está claro.",
  validate: vZiele,
  build(ctx) {
    const s = ctx.store;
    return h(
      "div",
      {},
      card(
        h("div", { class: "card__title", text: "¿Qué contacto has tenido hasta ahora?" }),
        h("p", { class: "card__hint", text: "Con finanzas, seguros o ayudas. Opcional." }),
        chipGroup(s, "ziele.beruehrungspunkte", OPT_BERUEHRUNGSPUNKTE)
      ),
      card(
        h("div", { class: "card__title", html: "¿Qué es especialmente importante para ti en una asesoría? <span style='color:var(--neg)'>*</span>" }),
        h("p", { class: "card__hint", text: "Elige al menos una opción." }),
        chipGroup(s, "ziele.wichtig", OPT_WICHTIG)
      ),
      card(
        h("div", { class: "card__title", html: "¿Qué quieres lograr con tu planificación financiera? <span style='color:var(--neg)'>*</span>" }),
        h("p", { class: "card__hint", text: "Elige al menos una opción." }),
        chipGroup(s, "ziele.ziele", OPT_ZIELE),
        h("div", { style: { marginTop: "16px" } },
          textareaInput(s, "ziele.zieleFreitext", {
            label: "¿Quieres contarnos algo más sobre tus objetivos, deseos o sueños?",
            optional: true,
            optionalText: "Opcional: complétalo solo si quieres.",
            placeholder: "Tus ideas …",
            rows: 3,
          }))
      )
    );
  },
};

/* ---------- 3 · Datos personales ---------- */
export const stepPersoenlich: Step = {
  id: "persoenlich",
  section: "fundament",
  eyebrow: "Fundamentos",
  title: "Datos personales",
  subtitle: "Para saber con quién estamos hablando.",
  validate: vStammdaten,
  build(ctx) {
    const s = ctx.store;
    return card(
      h("div", { class: "grid-2" },
        textInput(s, "stammdaten.vorname", { label: "Nombre", required: true }),
        textInput(s, "stammdaten.nachname", { label: "Apellido", required: true })),
      h("div", { class: "grid-2" },
        textInput(s, "stammdaten.geburtsdatum", { label: "Fecha de nacimiento", required: true, type: "date" }),
        textInput(s, "stammdaten.geburtsort", { label: "Lugar de nacimiento", required: true })),
      textInput(s, "stammdaten.staatsangehoerigkeit", { label: "Nacionalidad", required: true }),
      h("div", { class: "grid-2" },
        textInput(s, "stammdaten.strasse", { label: "Calle", required: true }),
        textInput(s, "stammdaten.hausnummer", { label: "Número", required: true })),
      h("div", { class: "grid-2" },
        textInput(s, "stammdaten.plz", { label: "Código postal", required: true, inputmode: "numeric" }),
        textInput(s, "stammdaten.ort", { label: "Ciudad", required: true })),
      h("div", { class: "grid-2" },
        textInput(s, "stammdaten.email", { label: "Correo electrónico", required: true, type: "email", inputmode: "email" }),
        textInput(s, "stammdaten.mobil", { label: "Número de móvil", required: true, type: "tel", inputmode: "tel" })),
      selectInput(s, "stammdaten.familienstand", OPT_FAMILIENSTAND, { label: "Estado civil", required: true })
    );
  },
};

/* ---------- 4 · Profesión y familia ---------- */
export const stepBerufFamilie: Step = {
  id: "beruf-familie",
  section: "fundament",
  eyebrow: "Fundamentos",
  title: "Profesión y familia",
  subtitle: "Algunos datos sobre tu entorno.",
  leaveMessage: "Tu base está lista.",
  validate: vBerufFamilie,
  build(ctx) {
    const s = ctx.store;
    const kinderDetail = h("div");
    const renderKinder = () => {
      kinderDetail.innerHTML = "";
      if (s.get("stammdaten.hatKinder") === "ja") {
        kinderDetail.appendChild(
          h("div", { class: "grid-2", style: { marginTop: "12px" } },
            textInput(s, "stammdaten.anzahlKinder", { label: "Número de hijos", required: true, type: "number", inputmode: "numeric" }),
            textInput(s, "stammdaten.alterKinder", { label: "Edad de los hijos", required: true, placeholder: "p. ej. 4 y 7" }))
        );
      }
    };
    renderKinder();

    // La profesión solo se pide si la persona ejerce alguna actividad.
    const berufDetail = h("div");
    const renderBeruf = () => {
      berufDetail.innerHTML = "";
      if (s.get("stammdaten.berufsstatus") === OHNE_TAETIGKEIT) {
        // Si no, quedaría guardada una actividad escrita antes.
        if (s.get("stammdaten.berufsbezeichnung")) s.set("stammdaten.berufsbezeichnung", "");
      } else {
        berufDetail.appendChild(
          textInput(s, "stammdaten.berufsbezeichnung", {
            label: "Profesión / actividad actual",
            required: true,
            placeholder: "p. ej. mecánica industrial",
            hint: "Sé lo más preciso posible: tu actividad exacta determina tu protección.",
          })
        );
      }
    };
    renderBeruf();

    return h(
      "div",
      {},
      card(
        h("div", { class: "card__title", html: "¿Tienes hijos? <span style='color:var(--neg)'>*</span>" }),
        jaNein(s, "stammdaten.hatKinder", renderKinder),
        kinderDetail
      ),
      card(
        h("div", { class: "card__title", html: "Situación profesional <span style='color:var(--neg)'>*</span>" }),
        h("p", { class: "card__hint", text: "Necesitamos estos datos para entender bien tu evolución de ingresos y tu necesidad de protección." }),
        selectInput(s, "stammdaten.berufsstatus", OPT_BERUFSSTATUS, { label: "Situación actual", required: true, onInput: renderBeruf }),
        berufDetail,
        textInput(s, "stammdaten.abschluss", { label: "Título escolar o universitario", required: true, placeholder: "p. ej. bachillerato, formación profesional, grado" })
      ),
      note("info-dark", "Tu situación profesional determina en gran medida qué protección encaja contigo; por eso preguntamos con más detalle.")
    );
  },
};
